import logger from '../utils/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Heart, Award, Cake, Sparkles, Plus, Send } from 'lucide-react';

const REACTIONS = ['+1', '🎉', '🔥', '👏', '❤️'];
const VALUES = ['Customer First', 'Ownership', 'Speed', 'Collaboration', 'Innovation', 'Excellence'];

const Engagement = () => {
    const { user, canManage } = useAuth();
    const [feed, setFeed] = useState([]);
    const [leaderboard, setLeaderboard] = useState({ top_recipients: [] });
    const [pulse, setPulse] = useState(null);
    const [birthdays, setBirthdays] = useState({ birthdays: [], anniversaries: [] });
    const [employees, setEmployees] = useState([]);
    const [tab, setTab] = useState('kudos');
    const [pulseAnswers, setPulseAnswers] = useState({});
    const [kudosOpen, setKudosOpen] = useState(false);
    const [kudosForm, setKudosForm] = useState({ to_employee_id: '', message: '', value: '', is_public: true });

    const fetchAll = useCallback(async () => {
        try {
            const [f, lb, p, b, e] = await Promise.all([
                axios.get(`${API_URL}/api/kudos/feed?limit=50`, { withCredentials: true }),
                axios.get(`${API_URL}/api/kudos/leaderboard?days=30`, { withCredentials: true }),
                axios.get(`${API_URL}/api/pulse/surveys/active`, { withCredentials: true }),
                axios.get(`${API_URL}/api/birthdays/upcoming?days=30`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
            ]);
            setFeed(f.data || []);
            setLeaderboard(lb.data || {});
            setPulse(p.data);
            setBirthdays(b.data || {});
            setEmployees(e.data || []);
        } catch (er) { logger.error('engagement fetch', er); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const sendKudos = async () => {
        if (!kudosForm.to_employee_id || !kudosForm.message) { alert('Pick a recipient + message'); return; }
        try {
            await axios.post(`${API_URL}/api/kudos`, kudosForm, { withCredentials: true });
            setKudosForm({ to_employee_id: '', message: '', value: '', is_public: true });
            setKudosOpen(false);
            fetchAll();
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const react = async (kudosId, emoji) => {
        try {
            await axios.post(`${API_URL}/api/kudos/${kudosId}/react?emoji=${encodeURIComponent(emoji)}`, {}, { withCredentials: true });
            fetchAll();
        } catch (e) { logger.error('react', e); }
    };

    const submitPulse = async () => {
        if (!pulse) return;
        try {
            await axios.post(`${API_URL}/api/pulse/surveys/${pulse._id}/respond`, { answers: pulseAnswers }, { withCredentials: true });
            setPulse(null);
            setPulseAnswers({});
            alert('Thanks — your input is anonymous and helps us improve!');
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
        <div className="space-y-6" data-testid="engagement-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Engagement Hub</h1>
                    <p className="text-slate-500 mt-1">Recognition, pulse, celebrations — the heartbeat of your team.</p>
                </div>
                <Dialog open={kudosOpen} onOpenChange={setKudosOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-lg shadow-sm" data-testid="give-kudos-btn"><Heart className="h-4 w-4 mr-2" />Give Kudos</Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl max-w-md">
                        <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Give Kudos 🎉</DialogTitle><DialogDescription>Public peer-to-peer recognition. Goes to dashboard + recipient gets a notification.</DialogDescription></DialogHeader>
                        <div className="space-y-3 mt-3">
                            <div><Label className="text-xs font-bold uppercase text-slate-500">To</Label>
                                <select value={kudosForm.to_employee_id} onChange={e => setKudosForm({ ...kudosForm, to_employee_id: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1" data-testid="kudos-recipient">
                                    <option value="">Select teammate</option>
                                    {employees.filter(emp => emp._id !== user._id).map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.designation || emp.role})</option>)}
                                </select>
                            </div>
                            <div><Label className="text-xs font-bold uppercase text-slate-500">Value lived</Label>
                                <select value={kudosForm.value} onChange={e => setKudosForm({ ...kudosForm, value: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                    <option value="">— optional —</option>
                                    {VALUES.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                            </div>
                            <div><Label className="text-xs font-bold uppercase text-slate-500">Message *</Label><Textarea value={kudosForm.message} onChange={e => setKudosForm({ ...kudosForm, message: e.target.value })} rows={3} maxLength={500} className="rounded-lg mt-1 bg-slate-50 border-slate-200" placeholder="What did they do? Be specific!" data-testid="kudos-message" /></div>
                            <div className="flex items-center gap-2"><input id="public" type="checkbox" checked={kudosForm.is_public} onChange={e => setKudosForm({ ...kudosForm, is_public: e.target.checked })} /><label htmlFor="public" className="text-xs text-slate-600">Show on public feed</label></div>
                            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-lg" onClick={() => setKudosOpen(false)}>Cancel</Button><Button onClick={sendKudos} className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-lg shadow-sm" data-testid="kudos-send-btn"><Send className="h-4 w-4 mr-2" />Send</Button></div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Pulse Survey banner — if active and not yet responded */}
            {pulse && (
                <Card className="rounded-xl shadow-sm border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 overflow-hidden" data-testid="pulse-banner">
                    <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                            <Sparkles className="h-6 w-6 text-purple-600 mt-1 shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-bold text-slate-900 text-base">{pulse.title}</h3>
                                <p className="text-xs text-slate-600 mt-1">Anonymous · Takes &lt; 30 seconds</p>
                                <div className="space-y-3 mt-4">
                                    {pulse.questions.map(q => (
                                        <div key={q.id}>
                                            <Label className="text-sm font-medium text-slate-700">{q.question}</Label>
                                            {q.type === 'scale' && (
                                                <div className="flex gap-2 mt-2">
                                                    {[1, 2, 3, 4, 5].map(n => (
                                                        <button key={n} onClick={() => setPulseAnswers({ ...pulseAnswers, [q.id]: n })} className={`w-10 h-10 rounded-lg border-2 font-bold transition-all ${pulseAnswers[q.id] === n ? 'bg-purple-600 text-white border-purple-700 scale-110' : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300'}`} data-testid={`pulse-${q.id}-${n}`}>{n}</button>
                                                    ))}
                                                </div>
                                            )}
                                            {q.type === 'yes_no' && (
                                                <div className="flex gap-2 mt-2">
                                                    {['yes', 'no'].map(v => <button key={v} onClick={() => setPulseAnswers({ ...pulseAnswers, [q.id]: v })} className={`px-4 py-2 rounded-lg border font-medium ${pulseAnswers[q.id] === v ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 border-slate-200'}`}>{v}</button>)}
                                                </div>
                                            )}
                                            {q.type === 'text' && <Textarea value={pulseAnswers[q.id] || ''} onChange={e => setPulseAnswers({ ...pulseAnswers, [q.id]: e.target.value })} rows={2} className="rounded-lg mt-2 bg-white border-slate-200" />}
                                        </div>
                                    ))}
                                </div>
                                <Button onClick={submitPulse} disabled={Object.keys(pulseAnswers).length < pulse.questions.length} className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-sm mt-4" data-testid="pulse-submit-btn">Submit anonymously</Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Kudos Feed */}
                <div className="lg:col-span-2 space-y-4">
                    <h2 className="text-base font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}><Heart className="h-4 w-4 text-pink-500" /> Recognition Feed</h2>
                    {feed.length === 0 && <Card className="rounded-xl shadow-sm"><CardContent className="p-12 text-center text-slate-400">No kudos yet. Be the first 💪</CardContent></Card>}
                    {feed.map(k => (
                        <Card key={k._id} className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-testid={`kudos-${k._id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm shrink-0">{(k.from_name || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()}</div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm"><strong>{k.from_name}</strong> <span className="text-slate-500">→</span> <strong>{k.to_name}</strong>{k.value && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">{k.value}</span>}</p>
                                        <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{k.message}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">{fmtDate(k.created_at)}</p>
                                        <div className="flex gap-1 mt-2">
                                            {REACTIONS.map(emoji => {
                                                const count = (k.reactions?.[emoji] || []).length;
                                                const active = (k.reactions?.[emoji] || []).includes(user._id);
                                                return (
                                                    <button key={emoji} onClick={() => react(k._id, emoji)} className={`text-xs px-2 py-0.5 rounded-full border ${active ? 'bg-pink-50 border-pink-300' : 'bg-white border-slate-200'} hover:bg-pink-50`} data-testid={`react-${k._id}-${emoji}`}>
                                                        {emoji} {count > 0 && <span className="font-medium ml-0.5">{count}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Sidebar: Leaderboard + Birthdays */}
                <div className="space-y-4">
                    <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                        <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-pink-50 to-rose-50 py-3">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}><Award className="h-4 w-4 text-pink-500" /> Top Recognized · 30d</CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                            {leaderboard.top_recipients?.length === 0 ? <p className="text-xs text-slate-400 text-center py-3">No data yet.</p> :
                                <ol className="space-y-2">
                                    {leaderboard.top_recipients.slice(0, 5).map((r, i) => (
                                        <li key={r.employee_id} className="flex items-center gap-2 text-sm">
                                            <span className="text-base">{['🥇', '🥈', '🥉', '4️⃣', '5️⃣'][i]}</span>
                                            <span className="flex-1 truncate font-medium">{r.employee_name}</span>
                                            <span className="text-xs text-slate-500 font-mono">{r.kudos_count}</span>
                                        </li>
                                    ))}
                                </ol>
                            }
                        </CardContent>
                    </Card>

                    <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                        <CardHeader className="border-b border-slate-200 bg-gradient-to-r from-amber-50 to-orange-50 py-3">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}><Cake className="h-4 w-4 text-amber-600" /> Upcoming · 30d</CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 space-y-3">
                            {(birthdays.birthdays || []).length === 0 && (birthdays.anniversaries || []).length === 0 && <p className="text-xs text-slate-400 text-center py-3">No celebrations coming up.</p>}
                            {(birthdays.birthdays || []).slice(0, 5).map(b => (
                                <div key={`b-${b.employee_id}`} className="flex items-center gap-2 text-xs" data-testid={`birthday-${b.employee_id}`}>
                                    <span className="text-base">🎂</span>
                                    <span className="flex-1 truncate"><strong>{b.name}</strong> · {b.designation}</span>
                                    <span className="text-slate-500 font-mono">{b.is_today ? 'Today!' : `${b.days_until}d`}</span>
                                </div>
                            ))}
                            {(birthdays.anniversaries || []).slice(0, 5).map(a => (
                                <div key={`a-${a.employee_id}`} className="flex items-center gap-2 text-xs" data-testid={`anniversary-${a.employee_id}`}>
                                    <span className="text-base">🎊</span>
                                    <span className="flex-1 truncate"><strong>{a.name}</strong> · {a.years}y</span>
                                    <span className="text-slate-500 font-mono">{a.is_today ? 'Today!' : `${a.days_until}d`}</span>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Engagement;
